import * as v from 'valibot';
import type { PaneVisibilitySnapshot, ViewMode } from '@mobile-mapping/web3d';
import type { PaneManagerConfig } from '@trimble/pane-layout';
import { DrawingTool } from '@trimble/geospatial';
import {
  vSectionPlane,
  vSectionBox,
  type SectionPlane,
  type SectionBox,
} from '@data-access/connect/tc-tcps';
import type { PositioningData } from '@trimble/web3d-providers';
import type { Point3D, RGBFormat } from '@trimble/utilities';
import type { StartNavigationOptions } from '@technology/web3d-plugin-trimbim';

export const MULTIPANE_RESERVED_DATA_VERSION = '1.3' as const;

/** Matches the TCPS putReservedViewData body limit. */
// eslint-disable-next-line no-magic-numbers
export const MULTIPANE_RESERVED_DATA_MAX_CHARS = 500 * 1024;

export interface MultipaneClippingConfig {
  readonly sectionBox: SectionBox | null;
  readonly sectionPlanes: SectionPlane[];
}

export interface SavedStationIdentity {
  readonly runId: string;
  readonly localStationIndex: number;
}

export interface SavedAlignmentIdentity {
  readonly alignmentId: string;
}

/** Shared RGB type plus the optional alpha the measurement color allows. */
export type ReservedMeasurementColor = Readonly<RGBFormat> & {
  readonly a?: number;
};

/**
 * Serializable alignment measurement persisted in reserved view data, since
 * Connect Views have no native alignment markup type (NUMARK-968).
 */
export interface ReservedAlignmentMeasurement {
  readonly type: 'alignment';
  readonly id: string;
  readonly value: number;
  readonly ground: Point3D;
  readonly point: Point3D;
  readonly alignment: Point3D;
  readonly label?: string;
  readonly color?: ReservedMeasurementColor;
  readonly hidden?: boolean;
}

/** Discriminated union of measurement types persisted in reserved view data. */
export type ReservedMeasurement = ReservedAlignmentMeasurement;

// Point/line/angle geometry is persisted natively in Connect, but markups carry
// no name field, so names are patched separately and merged back by markup id.
export interface ConnectMeasurementData {
  readonly id: string;
  readonly label: string;
}

/**
 * Per-pane navigation mode for save/restore. Alignment and station
 * identities are shared across panes and live at the top level on
 * {@link MultipaneReservedData}.
 */
export type PaneNavigationState =
  | { readonly kind: 'freeView' }
  | {
      readonly kind: 'alignmentNavigation';
      readonly options: StartNavigationOptions;
    }
  | { readonly kind: 'station' };

export interface MultipaneCellContentConfig {
  readonly camera: PositioningData;
  readonly clipping: MultipaneClippingConfig;
  readonly viewMode: ViewMode;
  readonly featureToggles: Record<string, boolean>;
  readonly navigationState?: PaneNavigationState;
  readonly paneVisibility?: PaneVisibilitySnapshot;
}

export interface SavedCadEditingState {
  readonly cadEditingMode: boolean;
  readonly selectedLayerId?: string;
  readonly desiredDrawingTool?: DrawingTool;
  readonly visibleLayerIds?: readonly string[];
}

export interface MultipaneReservedData {
  readonly layout: PaneManagerConfig<MultipaneCellContentConfig>;
  readonly version: string;
  readonly station?: SavedStationIdentity;
  readonly alignment?: SavedAlignmentIdentity;
  readonly loadedTrajectoryIds?: readonly string[];
  readonly measurements?: readonly ReservedMeasurement[];
  readonly connectMeasurementData?: readonly ConnectMeasurementData[];
  /** Saved even when CAD is off, so loading the view can switch off active CAD. */
  readonly cadEditing?: SavedCadEditingState;
}

const Point3DSchema = v.object({
  x: v.number(),
  y: v.number(),
  z: v.number(),
});

const Point2DSchema = v.object({
  x: v.number(),
  y: v.number(),
});

const QuaternionTupleSchema = v.tuple([
  v.number(),
  v.number(),
  v.number(),
  v.number(),
]);

const PositioningDataSchema = v.object({
  cameraPosition: v.union([Point3DSchema, Point2DSchema, v.null()]),
  targetPosition: v.union([Point3DSchema, Point2DSchema, v.null()]),
  quaternion: v.union([QuaternionTupleSchema, v.null()]),
  fov: v.union([v.number(), v.null()]),
});

const MultipaneClippingConfigSchema = v.object({
  sectionBox: v.union([vSectionBox, v.null()]),
  sectionPlanes: v.array(vSectionPlane),
});

const ViewModeSchema = v.variant('type', [
  v.object({ type: v.literal('2D') }),
  v.object({ type: v.literal('3D') }),
  v.object({ type: v.literal('Panorama') }),
  v.object({ type: v.literal('Image'), cameraIndex: v.number() }),
]);

/** Third-party shape; validated at apply time. */
const StartNavigationOptionsSchema = v.looseObject({});

const PaneNavigationStateSchema = v.variant('kind', [
  v.object({ kind: v.literal('freeView') }),
  v.object({
    kind: v.literal('alignmentNavigation'),
    options: StartNavigationOptionsSchema,
  }),
  v.object({ kind: v.literal('station') }),
]);

const LinkableSliderSnapshotSchema = v.object({
  local: v.union([v.number(), v.null()]),
  linked: v.boolean(),
});

const LinkableRangeSnapshotSchema = v.object({
  local: v.number(),
  linked: v.boolean(),
});

const PaneVisibilitySnapshotSchema = v.object({
  models: LinkableSliderSnapshotSchema,
  pointClouds: LinkableSliderSnapshotSchema,
  stations: LinkableSliderSnapshotSchema,
  image: LinkableSliderSnapshotSchema,
  clipDistance: LinkableRangeSnapshotSchema,
});

const MultipaneCellContentConfigSchema = v.object({
  camera: PositioningDataSchema,
  clipping: MultipaneClippingConfigSchema,
  viewMode: ViewModeSchema,
  featureToggles: v.record(v.string(), v.boolean()),
  navigationState: v.optional(PaneNavigationStateSchema),
  paneVisibility: v.optional(PaneVisibilitySnapshotSchema),
});

const PaneUIConfigSchema = v.object({
  size: v.number(),
  minSize: v.number(),
});

const PaneManagerConfigSchema = v.object({
  label: v.string(),
  paneRows: v.array(
    v.object({
      config: PaneUIConfigSchema,
      cells: v.array(
        v.object({
          size: v.number(),
          minSize: v.number(),
          contentConfig: MultipaneCellContentConfigSchema,
        })
      ),
    })
  ),
});

const SavedStationIdentitySchema = v.object({
  runId: v.string(),
  localStationIndex: v.number(),
});

const SavedAlignmentIdentitySchema = v.object({
  alignmentId: v.string(),
});

const ReservedMeasurementColorSchema = v.object({
  r: v.number(),
  g: v.number(),
  b: v.number(),
  a: v.optional(v.number()),
});

const ReservedAlignmentMeasurementSchema = v.object({
  type: v.literal('alignment'),
  id: v.string(),
  value: v.number(),
  ground: Point3DSchema,
  point: Point3DSchema,
  alignment: Point3DSchema,
  label: v.optional(v.string()),
  color: v.optional(ReservedMeasurementColorSchema),
  hidden: v.optional(v.boolean()),
});

const ReservedMeasurementSchema = v.variant('type', [
  ReservedAlignmentMeasurementSchema,
]);

const ConnectMeasurementDataSchema = v.object({
  id: v.string(),
  label: v.string(),
});

const SavedCadEditingStateSchema = v.object({
  cadEditingMode: v.boolean(),
  selectedLayerId: v.optional(v.string()),
  desiredDrawingTool: v.optional(v.enum(DrawingTool)),
  visibleLayerIds: v.optional(v.array(v.string())),
});

export const MultipaneReservedDataSchema = v.object({
  layout: PaneManagerConfigSchema,
  version: v.string(),
  station: v.optional(SavedStationIdentitySchema),
  alignment: v.optional(SavedAlignmentIdentitySchema),
  loadedTrajectoryIds: v.optional(v.array(v.string())),
  measurements: v.optional(v.array(ReservedMeasurementSchema)),
  connectMeasurementData: v.optional(v.array(ConnectMeasurementDataSchema)),
  cadEditing: v.fallback(v.optional(SavedCadEditingStateSchema), undefined),
});
